import React from 'react';
import PropTypes from 'prop-types';
import {Label} from '../../Components/Label';
import {OptionIndex} from '../../Components/OptionIndex';
import {OptionFeedback} from '../../Components/OptionFeedback';
import {OptionWrapper} from '../../Components/OptionWrapper';
import {OverlaySlider} from '../../Components/OverlaySlider';
import {SliderGroup} from '../../Components/SliderGroup';
import {Utilities} from '@learningblocks/exp-constants';

const {COMPONENT_TYPE} = Utilities;

/**
 * Option:
 *  - Option Component for SingleSelect Component
 */
export function Option(props) {
  const selectOption = () => {
    props.disabled === false &&
      props.selectOption({
        optionID: props.id,
        checked: !props.selected,
      });
  };

  const getOptionClassName = () => {
    if (props.selected && props.disabled) {
      if (props.isCorrect) return 'cscq__option correct';
      else return 'cscq__option incorrect';
    } else if (props.selected) return 'cscq__option selected';
    else return 'cscq__option';
  };

  const getFeedbackClassName = () => {
    if (props.isCorrect) return 'cscq__feedback_correct';
    else return 'cscq__feedback_incorrect';
  };

  const renderOverlaySlider = () => (
    <OverlaySlider
      sequence={props.sequence}
      highlight={[props.current.start, props.current.end - props.current.start]}
      highlightClass={props.highlightClass}
      onChange={() => {}}
      disable={true}
    />
  );

  const renderSequenceSliderGroup = () => (
    <SliderGroup
      id={props.id}
      sliders={props.sliders}
      changeHighlight={() => {}}
    />
  );

  const renderLabel = () => <Label label={props.text} id={props.id} />;

  const renderOptionContent = ({type}) => {
    switch (type) {
      case COMPONENT_TYPE.SEQUENCE_SLIDER:
        return renderOverlaySlider();
      case COMPONENT_TYPE.SEQUENCE_SLIDER_GROUP:
        return renderSequenceSliderGroup();
      case COMPONENT_TYPE.LABEL:
      default:
        return renderLabel();
    }
  };

  return (
    <>
      <OptionWrapper
        className={getOptionClassName()}
        selectOption={selectOption}>
        <OptionIndex
          id={props.id}
          index={props.index}
          selected={props.selected}
          disabled={props.disabled}
          isCorrect={props.isCorrect}
        />
        {renderOptionContent(props)}
      </OptionWrapper>
      <OptionFeedback
        visible={props.disabled}
        className={getFeedbackClassName()}
        text={props.feedback}
      />
    </>
  );
}

Option.propTypes = {
  /**
   * Custom className to be set on component
   */
  className: PropTypes.string,
  /**
   * The feedback to be displayed for the particular option
   */
  feedback: PropTypes.string,
  /**
   * ID for the particular prop
   */
  id: PropTypes.number.isRequired,
  /**
   * Boolean value specifying if the option is correct
   */
  isCorrect: PropTypes.bool.isRequired,
  /**
   * Boolean value specifying if the option is selected
   */
  selected: PropTypes.bool,
  /**
   * Boolean value specifying if the option is disabled
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * Callback function to be involked when selecting the option
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The currently highlighted content
   */
  current: PropTypes.shape({
    /**
     * Text to be displayed in slider select
     */
    text: PropTypes.string,
    /**
     * The starting point of the highlighted text
     */
    start: PropTypes.string,
  }),
  /**
   * The string sequence to be displayed.
   */
  sequence: PropTypes.string,
  /**
   * The array to set the color of highlighting
   */
  highlightClass: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  /**
   * Text for label
   */
  text: PropTypes.string,
  /**
   * for keyboard navigation
   */
  tabIndex: PropTypes.string,
};

Option.defaultProps = {className: ''};
